import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
    Coins, Zap, TrendingUp, Users, Shield, Flame, ArrowRight, Trophy,
    Target, Sparkles, ChevronRight, Lock, Globe, Wallet, Vote, Gift,
    PieChart, Calendar, HelpCircle,
} from 'lucide-react';
import { CountdownToLaunch } from '../components/CountdownToLaunch';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EARLY_BIRD_LIMIT = 10000;

export default function B4PLaunchPage() {
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [holders, setHolders] = useState(0);

    useEffect(() => {
        fetch(`${API_URL}/api/sponsor/stats`).then(r => r.ok && r.json()).then(d => {
            if (d) {
                setStats(d);
                setHolders(d.total_sponsors || 0);
            }
        }).catch(() => {});
        fetch(`${API_URL}/api/b4p/stats`).then(r => r.ok && r.json()).then(d => {
            if (d) setStats(s => ({ ...(s || {}), ...d }));
        }).catch(() => {});
    }, []);

    const remaining = Math.max(0, EARLY_BIRD_LIMIT - holders);
    const progress = Math.min(100, (holders / EARLY_BIRD_LIMIT) * 100);

    const valueDrivers = [
        { icon: Lock, key: 'stake', text: t('b4pLaunch.driverStake') },
        { icon: Coins, key: 'cashback', text: t('b4pLaunch.driverCashback') },
        { icon: Users, key: 'refer', text: t('b4pLaunch.driverRefer') },
        { icon: Vote, key: 'gov', text: t('b4pLaunch.driverGov') },
        { icon: TrendingUp, key: 'revenue', text: t('b4pLaunch.driverRevenue') },
        { icon: Flame, key: 'deflate', text: t('b4pLaunch.driverDeflation') },
    ];

    const earnPaths = [
        { num: '01', title: t('b4pLaunch.earn1Title'), desc: t('b4pLaunch.earn1Desc') },
        { num: '02', title: t('b4pLaunch.earn2Title'), desc: t('b4pLaunch.earn2Desc') },
        { num: '03', title: t('b4pLaunch.earn3Title'), desc: t('b4pLaunch.earn3Desc') },
        { num: '04', title: t('b4pLaunch.earn4Title'), desc: t('b4pLaunch.earn4Desc') },
    ];

    return (
        <div className="min-h-screen">
            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute inset-0 bg-gradient-to-br from-[#FFD700]/15 via-transparent to-[#0066CC]/10" />
                <div className="absolute -top-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#FFD700]/20 blur-3xl" />
                <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#0066CC]/20 blur-3xl" />

                <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 md:py-28 text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFD700]/15 border border-[#FFD700]/30 mb-6">
                        <Sparkles className="w-3.5 h-3.5 text-[#FFD700]" />
                        <span className="text-[#FFD700] text-xs font-bold uppercase tracking-widest">{t('b4pLaunch.heroBadge')}</span>
                    </div>
                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black text-white mb-4 leading-[1.05]">
                        B4P
                        <span className="block text-2xl sm:text-3xl md:text-4xl font-bold text-[#FFD700] mt-2">{t('b4pLaunch.heroTitle')}</span>
                    </h1>
                    <p className="text-xl sm:text-2xl text-white/85 max-w-3xl mx-auto leading-relaxed mb-3">
                        {t('b4pLaunch.heroP1')}
                    </p>
                    <p className="text-lg text-white/60 max-w-2xl mx-auto mb-8">
                        {t('b4pLaunch.heroP2')}
                    </p>

                    {/* CTAs */}
                    <div className="flex flex-col sm:flex-row gap-3 justify-center mb-10">
                        <Link to="/presale">
                            <Button
                                size="lg"
                                className="bg-[#FFD700] text-[#001A4D] hover:bg-[#FFC700] font-bold px-8 py-6 text-lg shadow-[0_0_40px_rgba(255,215,0,0.35)]"
                                data-testid="b4p-buy-now"
                            >
                                <Wallet className="w-5 h-5 mr-2" />
                                {t('b4pLaunch.ctaBuy')}
                            </Button>
                        </Link>
                        <Link to="/bets">
                            <Button
                                size="lg"
                                variant="outline"
                                className="border-white/30 text-white hover:bg-white/5 font-semibold px-8 py-6 text-lg"
                                data-testid="b4p-explore-platform"
                            >
                                {t('b4pLaunch.ctaExplore')}
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                    </div>

                    {/* Big Bang Countdown */}
                    <div className="mb-10">
                        <CountdownToLaunch />
                    </div>

                    {/* Early Bird counter */}
                    <Card className="max-w-md mx-auto bg-black/30 border-[#FFD700]/30 backdrop-blur">
                        <CardContent className="p-4">
                            <div className="flex justify-between text-sm mb-2">
                                <span className="text-white/70 font-semibold">{t('b4pLaunch.earlyAccess')}</span>
                                <span className="text-[#FFD700] font-mono font-bold">{remaining.toLocaleString()} / {EARLY_BIRD_LIMIT.toLocaleString()}</span>
                            </div>
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                                <div className="h-full bg-gradient-to-r from-[#FFD700] to-[#FFA500] transition-all" style={{ width: `${progress}%` }} />
                            </div>
                            <div className="text-xs text-white/50 mt-2">{t('b4pLaunch.earlySpotsLeft', { count: remaining })}</div>
                        </CardContent>
                    </Card>
                </div>
            </section>

            {/* PROJECT HUB — Navigate to all B4P sub-pages */}
            <section className="py-16 border-b border-white/10 bg-gradient-to-b from-transparent via-white/[0.02] to-transparent">
                <div className="max-w-5xl mx-auto px-4 sm:px-6">
                    <h2 className="text-2xl sm:text-3xl font-black text-white text-center mb-2">
                        {t('b4pLaunch.hubTitle')}
                    </h2>
                    <p className="text-white/60 text-center mb-10 text-sm">{t('b4pLaunch.hubDesc')}</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <Link to="/presale" data-testid="hub-presale" className="group">
                            <Card className="bg-gradient-to-br from-[#FFD700]/15 to-white/[0.04] border-[#FFD700]/30 hover:border-[#FFD700] transition-all h-full">
                                <CardContent className="p-5 text-center">
                                    <Flame className="w-7 h-7 text-[#FFD700] mx-auto mb-2 group-hover:scale-110 transition-transform" />
                                    <div className="font-bold text-white mb-1">{t('b4pLaunch.hubPresale')}</div>
                                    <div className="text-xs text-[#FFD700]/80">{t('b4pLaunch.hubPresaleSub')}</div>
                                </CardContent>
                            </Card>
                        </Link>
                        <Link to="/airdrop" data-testid="hub-airdrop" className="group">
                            <Card className="bg-gradient-to-br from-[#FF6B35]/15 to-white/[0.04] border-[#FF6B35]/30 hover:border-[#FF6B35] transition-all h-full">
                                <CardContent className="p-5 text-center">
                                    <Gift className="w-7 h-7 text-[#FF6B35] mx-auto mb-2 group-hover:scale-110 transition-transform" />
                                    <div className="font-bold text-white mb-1">{t('b4pLaunch.hubAirdrop')}</div>
                                    <div className="text-xs text-[#FF6B35]/80">{t('b4pLaunch.hubAirdropSub')}</div>
                                </CardContent>
                            </Card>
                        </Link>
                        <Link to="/tokenomics" data-testid="hub-tokenomics" className="group">
                            <Card className="bg-white/[0.04] border-white/10 hover:border-[#FFD700]/40 transition-all h-full">
                                <CardContent className="p-5 text-center">
                                    <PieChart className="w-7 h-7 text-[#FFD700] mx-auto mb-2 group-hover:scale-110 transition-transform" />
                                    <div className="font-bold text-white mb-1">{t('b4pLaunch.hubTokenomics')}</div>
                                    <div className="text-xs text-white/50">{t('b4pLaunch.hubTokenomicsSub')}</div>
                                </CardContent>
                            </Card>
                        </Link>
                        <Link to="/roadmap" data-testid="hub-roadmap" className="group">
                            <Card className="bg-white/[0.04] border-white/10 hover:border-[#9B6FE8]/40 transition-all h-full">
                                <CardContent className="p-5 text-center">
                                    <Calendar className="w-7 h-7 text-[#9B6FE8] mx-auto mb-2 group-hover:scale-110 transition-transform" />
                                    <div className="font-bold text-white mb-1">{t('b4pLaunch.hubRoadmap')}</div>
                                    <div className="text-xs text-white/50">{t('b4pLaunch.hubRoadmapSub')}</div>
                                </CardContent>
                            </Card>
                        </Link>
                        <Link to="/b4p" data-testid="hub-staking" className="group">
                            <Card className="bg-white/[0.04] border-white/10 hover:border-[#00FF66]/40 transition-all h-full">
                                <CardContent className="p-5 text-center">
                                    <Lock className="w-7 h-7 text-[#00FF66] mx-auto mb-2 group-hover:scale-110 transition-transform" />
                                    <div className="font-bold text-white mb-1">{t('b4pLaunch.hubStaking')}</div>
                                    <div className="text-xs text-white/50">{t('b4pLaunch.hubStakingSub')}</div>
                                </CardContent>
                            </Card>
                        </Link>
                        <Link to="/faq" data-testid="hub-faq" className="group">
                            <Card className="bg-white/[0.04] border-white/10 hover:border-[#0066CC]/40 transition-all h-full">
                                <CardContent className="p-5 text-center">
                                    <HelpCircle className="w-7 h-7 text-[#6FA8FF] mx-auto mb-2 group-hover:scale-110 transition-transform" />
                                    <div className="font-bold text-white mb-1">{t('b4pLaunch.hubFaq')}</div>
                                    <div className="text-xs text-white/50">{t('b4pLaunch.hubFaqSub')}</div>
                                </CardContent>
                            </Card>
                        </Link>
                    </div>
                </div>
            </section>

            {/* VALUE DRIVERS */}
            <section className="py-20 border-b border-white/10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white text-center mb-3">
                        {t('b4pLaunch.whyOwnTitle')} <span className="text-[#FFD700]">B4P</span> ?
                    </h2>
                    <p className="text-white/60 text-center max-w-2xl mx-auto mb-12">{t('b4pLaunch.whyOwnDesc')}</p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {valueDrivers.map((d) => {
                            const Icon = d.icon;
                            return (
                                <Card key={d.key} className="bg-white/[0.04] border-white/10 hover:border-[#FFD700]/40 transition-all group" data-testid={`b4p-driver-${d.key}`}>
                                    <CardContent className="p-6">
                                        <div className="w-11 h-11 rounded-xl bg-[#FFD700]/15 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                                            <Icon className="w-5 h-5 text-[#FFD700]" />
                                        </div>
                                        <p className="text-white/90 leading-relaxed">{d.text}</p>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* HOW B4P GAINS VALUE */}
            <section className="py-20 border-b border-white/10 bg-gradient-to-br from-[#001A4D] to-[#000814]">
                <div className="max-w-4xl mx-auto px-4 sm:px-6">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white text-center mb-3">
                        {t('b4pLaunch.flywheelTitle')}
                    </h2>
                    <p className="text-white/60 text-center mb-12">{t('b4pLaunch.flywheelDesc')}</p>

                    <div className="space-y-3">
                        {[
                            { num: '01', text: t('b4pLaunch.flywheel1'), color: '#00C853' },
                            { num: '02', text: t('b4pLaunch.flywheel2'), color: '#0066CC' },
                            { num: '03', text: t('b4pLaunch.flywheel3'), color: '#FFD700' },
                            { num: '04', text: t('b4pLaunch.flywheel4'), color: '#FF6B35' },
                        ].map((step, i, arr) => (
                            <div key={step.num} className="flex items-start gap-4">
                                <div className="flex flex-col items-center shrink-0">
                                    <div className="w-12 h-12 rounded-full flex items-center justify-center font-mono font-black text-[#001A4D] text-lg" style={{ backgroundColor: step.color }}>
                                        {step.num}
                                    </div>
                                    {i < arr.length - 1 && <div className="w-0.5 h-12 mt-1" style={{ background: `linear-gradient(180deg, ${step.color}, transparent)` }} />}
                                </div>
                                <div className="flex-1 bg-white/5 border border-white/10 rounded-2xl px-5 py-4">
                                    <p className="text-white/90 text-lg leading-relaxed">{step.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 text-center p-6 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-2xl">
                        <Trophy className="w-8 h-8 text-[#FFD700] mx-auto mb-2" />
                        <p className="text-2xl sm:text-3xl font-black text-white">{t('b4pLaunch.flywheelOutcome')}</p>
                    </div>
                </div>
            </section>

            {/* WAYS TO EARN */}
            <section className="py-20 border-b border-white/10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6">
                    <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white text-center mb-3">
                        {t('b4pLaunch.earnTitle')}
                    </h2>
                    <p className="text-white/60 text-center max-w-2xl mx-auto mb-12">{t('b4pLaunch.earnDesc')}</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {earnPaths.map((p) => (
                            <Card key={p.num} className="bg-white/[0.04] border-white/10 hover:border-[#00C853]/40 transition-all">
                                <CardContent className="p-6">
                                    <div className="text-[#00C853] font-mono text-sm font-bold mb-2">{p.num}</div>
                                    <h3 className="text-xl font-bold text-white mb-2">{p.title}</h3>
                                    <p className="text-white/60 leading-relaxed">{p.desc}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* SOCIAL PROOF */}
            <section className="py-20 border-b border-white/10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="bg-white/[0.04] border-white/10 text-center">
                            <CardContent className="p-6">
                                <Users className="w-6 h-6 text-[#00C853] mx-auto mb-2" />
                                <div className="text-3xl font-black text-white">{holders.toLocaleString()}</div>
                                <div className="text-xs text-white/50 uppercase tracking-wider">{t('b4pLaunch.statHolders')}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10 text-center">
                            <CardContent className="p-6">
                                <Zap className="w-6 h-6 text-[#FFD700] mx-auto mb-2" />
                                <div className="text-3xl font-black text-white">{stats?.total_volume_eth?.toFixed(1) ?? '0'}</div>
                                <div className="text-xs text-white/50 uppercase tracking-wider">{t('b4pLaunch.statVolume')}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10 text-center">
                            <CardContent className="p-6">
                                <Globe className="w-6 h-6 text-[#0066CC] mx-auto mb-2" />
                                <div className="text-3xl font-black text-white">3</div>
                                <div className="text-xs text-white/50 uppercase tracking-wider">{t('b4pLaunch.statChains')}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white/[0.04] border-white/10 text-center">
                            <CardContent className="p-6">
                                <Shield className="w-6 h-6 text-[#FF6B35] mx-auto mb-2" />
                                <div className="text-3xl font-black text-white">3%</div>
                                <div className="text-xs text-white/50 uppercase tracking-wider">{t('b4pLaunch.statFees')}</div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* FINAL CTA */}
            <section className="py-24">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
                    <Gift className="w-12 h-12 text-[#FFD700] mx-auto mb-4" />
                    <h2 className="text-4xl sm:text-5xl md:text-6xl font-black text-white mb-4 leading-tight">
                        {t('b4pLaunch.finalCtaTitle')}
                    </h2>
                    <p className="text-lg text-white/70 mb-8 max-w-xl mx-auto">{t('b4pLaunch.finalCtaDesc')}</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <Link to="/sponsor">
                            <Button size="lg" className="bg-[#FFD700] text-[#001A4D] hover:bg-[#FFC700] font-bold px-10 py-7 text-lg" data-testid="b4p-final-buy">
                                {t('b4pLaunch.finalCtaBuy')}
                                <ChevronRight className="w-5 h-5 ml-1" />
                            </Button>
                        </Link>
                        <Link to="/b4p">
                            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/5 font-semibold px-10 py-7 text-lg" data-testid="b4p-final-staking">
                                <Target className="w-5 h-5 mr-2" />
                                {t('b4pLaunch.finalCtaStaking')}
                            </Button>
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
